import { useState, useEffect, useRef } from 'react';
import { useServiceWithAuth } from '@/hooks/useServiceWithAuth.ts';
import { GlobalTimelogService } from '@/services/features/GlobalTimelogService.ts';

// Timer state interface
interface TimelogState {
  seconds: number;
  isRunning: boolean;
  isManuallyPaused: boolean;
  isTabInactive: boolean;
  timeLogId: number | null;
}

// Global timelog state (singleton for cross-navigation persistence)
let globalState: TimelogState = {
  seconds: 0,
  isRunning: false,
  isManuallyPaused: false,
  isTabInactive: false,
  timeLogId: null,
};

// Global intervals and API instance
let timerInterval: NodeJS.Timeout | null = null;
let currentUserId: number | null = null;
let currentGlobalTimelogService: GlobalTimelogService | null = null;
let isInitialized = false; // Track initialization globally
let isInitializing = false; // Prevent concurrent initialization

// State change listeners for UI updates
const listeners = new Set<() => void>();
const notifyListeners = () => {
  listeners.forEach(fn => fn());
  saveState(); // Save state on every change
};

// sessionStorage key for persistence
const TIMELOG_SESSION_KEY = 'global_timelog_session'; // For navigation persistence within session

// Save state to sessionStorage for navigation persistence within the same session
const saveState = () => {
  try {
    const stateToSave = {
      seconds: globalState.seconds,
      isRunning: globalState.isRunning,
      isManuallyPaused: globalState.isManuallyPaused,
      timeLogId: globalState.timeLogId,
      currentUserId,
      isInitialized,
      timestamp: Date.now()
    };
    sessionStorage.setItem(TIMELOG_SESSION_KEY, JSON.stringify(stateToSave));
  } catch (error) {
    // Error saving timer state
  }
};

// Load state from sessionStorage
const loadState = (): boolean => {
  try {
    const saved = sessionStorage.getItem(TIMELOG_SESSION_KEY);
    if (!saved) return false;
    
    const parsedState = JSON.parse(saved);
    
    // For sessionStorage, don't validate age since it's per-session
    // Just check if the state has required fields
    if (!parsedState.timeLogId || !parsedState.isInitialized) return false;
    
    // Restore state
    globalState = {
      seconds: parsedState.seconds || 0,
      isRunning: parsedState.isRunning || false,
      isManuallyPaused: parsedState.isManuallyPaused || false,
      isTabInactive: false, // Reset tab state on load
      timeLogId: parsedState.timeLogId,
    };
    
    currentUserId = parsedState.currentUserId;
    isInitialized = parsedState.isInitialized;
    
    return true;
  } catch (error) {
    sessionStorage.removeItem(TIMELOG_SESSION_KEY);
    return false;
  }
};

// Clear saved state
const clearState = () => {
  sessionStorage.removeItem(TIMELOG_SESSION_KEY);
  sessionStorage.removeItem('navigation_marker'); // Also clear navigation marker
};

// Global cleanup function that can be called from anywhere (e.g., AuthContext)
const globalTimerCleanup = () => {
  // Clear timer interval
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  
  // Reset global state
  globalState = {
    seconds: 0,
    isRunning: false,
    isManuallyPaused: false,
    isTabInactive: false,
    timeLogId: null,
  };
  
  // Clear user and initialization
  currentUserId = null;
  isInitialized = false;
  isInitializing = false;
  
  // Clear session storage
  clearState();
  
  // Notify all listeners
  notifyListeners();
  
};

// Initialize timelog API
const initializeService = (globalTimelogService: GlobalTimelogService) => {
  currentGlobalTimelogService = globalTimelogService;
};

// Start fresh timelog session
const startTimelog = async (userId: number): Promise<void> => {
  // Prevent concurrent initialization
  if (isInitializing) {
    return;
  }
  
  isInitializing = true;
  
  try {
    
    // Service is already initialized via useServiceWithAuth
    currentUserId = userId;
    
    // Create new timelog with 0 duration
    const response = await currentGlobalTimelogService!.startGlobalTimelog(userId);
    
    // Initialize global state
    globalState = {
      seconds: 0,
      isRunning: true,
      isManuallyPaused: false,
      isTabInactive: false,
      timeLogId: response.timeLogId,
    };
    
    // Mark as initialized
    isInitialized = true;
    
    // Start the timer
    startTimer();
    
  } catch (error) {
    isInitialized = false; // Reset on error
    throw error;
  } finally {
    isInitializing = false;
  }
};

// Start the timer interval
const startTimer = (): void => {
  if (timerInterval) {
    clearInterval(timerInterval);
  }
  
  timerInterval = setInterval(() => {
    // Only increment if running and not paused (manually or by tab)
    if (globalState.isRunning && !globalState.isManuallyPaused && !globalState.isTabInactive) {
      globalState.seconds++;
      
      // Call updateTimelog at exact 60-second intervals
      if (globalState.seconds % 60 === 0 && globalState.timeLogId && currentGlobalTimelogService && currentUserId) {
        updateTimelog(globalState.seconds, currentUserId);
      }
      
      notifyListeners();
    }
  }, 1000);
};

// Update timelog via API
const updateTimelog = async (duration: number, userId: number): Promise<void> => {
  if (!globalState.timeLogId || !currentGlobalTimelogService) return;
  
  try {
    await currentGlobalTimelogService.updateGlobalTimelog(userId, globalState.timeLogId, duration);
  } catch (error) {
    // Error updating timelog
  }
};

// Stop and reset timer
const stopTimer = (): void => {
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
  
  globalState = {
    seconds: 0,
    isRunning: false,
    isManuallyPaused: false,
    isTabInactive: false,
    timeLogId: null,
  };
  
  currentUserId = null;
  isInitialized = false; // Reset initialization flag
  isInitializing = false; // Reset initializing flag
  clearState(); // Clear sessionStorage
  notifyListeners();
};

// Toggle manual pause/resume
const togglePause = (): void => {
  if (!globalState.isRunning) return;
  
  globalState.isManuallyPaused = !globalState.isManuallyPaused;
  
  // When manually resuming, ensure tab state doesn't interfere
  if (!globalState.isManuallyPaused && !document.hidden) {
    globalState.isTabInactive = false;
  }
  
  notifyListeners();
  
  // Emit sync event to patient timer
  window.dispatchEvent(new CustomEvent('timerSync', { 
    detail: { source: 'global', isPaused: globalState.isManuallyPaused } 
  }));
};

// Set pause state directly (for sync from patient timer)
const setGlobalPauseState = (isPaused: boolean): void => {
  if (!globalState.isRunning) return;
  
  // Only change if state is different
  if (globalState.isManuallyPaused !== isPaused) {
    globalState.isManuallyPaused = isPaused;
    
    // When resuming, ensure tab state doesn't interfere
    if (!isPaused && !document.hidden) {
      globalState.isTabInactive = false;
    }
    
    notifyListeners();
  }
};

// Handle tab visibility changes
const handleTabVisibilityChange = (): void => {
  if (!globalState.isRunning) return;
  
  const isHidden = document.hidden;
  
  if (isHidden) {
    // Tab became inactive - pause timer
    globalState.isTabInactive = true;
  } else {
    // Tab became active - always clear tab inactive state
    // Manual pause state is independent and handled by togglePause
    globalState.isTabInactive = false;
    if (!globalState.isManuallyPaused) {
    }
  }
  
  notifyListeners();
};

// Format seconds to MM:SS
const formatTime = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
};

// Check if user has timelog access
const hasTimelogAccess = (userType: string): boolean => {
  return ['ES', 'CS', 'PE'].includes(userType);
};

// Main hook
export const useGlobalTimelog = () => {
  const [, forceUpdate] = useState(0);
  const wasLoggedIn = useRef(false);
  
  // Get service instance with proper auth context
  const globalTimelogService = useServiceWithAuth(GlobalTimelogService);
  
  // Get auth data from service context - use refs to avoid recreating objects
  const userId = globalTimelogService.context.userId;
  const userType = globalTimelogService.context.userType;
  const token = globalTimelogService.context.token;
  
  // Create stable user object
  const user = userId ? { id: userId.toString(), type: userType || '' } : null;
  
  // Subscribe to state changes
  useEffect(() => {
    const listener = () => forceUpdate(prev => prev + 1);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, []);
  
  // Initialize timelog on mount (once per session)
  useEffect(() => {
    // Only initialize for authorized users
    if (!userId || !token || !hasTimelogAccess(userType || '') || !globalTimelogService) {
      return;
    }
    
    // Initialize service
    initializeService(globalTimelogService);
    
    // Initialize only once per session using global flag
    if (!isInitialized) {
      // Check if this is a fresh page load vs navigation
      const isPageReload = !sessionStorage.getItem('navigation_marker');
      
      if (isPageReload) {
        // Fresh page load - always start fresh
        sessionStorage.setItem('navigation_marker', 'true'); // Mark that we've been here
        clearState(); // Clear any existing session data
        startTimelog(parseInt(userId.toString()));
      } else {
        // Navigation within session - try to restore state
        const loaded = loadState();
        
        if (loaded && globalState.timeLogId && currentUserId === parseInt(userId.toString())) {
          // State loaded successfully from navigation
          isInitialized = true;
        } else {
          // No saved state - start fresh
          startTimelog(parseInt(userId.toString()));
        }
      }
    }
  }, [userId, token, userType, globalTimelogService]);
  
  // Handle logout cleanup
  useEffect(() => {
    if (userId && token) {
      wasLoggedIn.current = true;
    } else if (wasLoggedIn.current) {
      // User logged out - stop timer
      stopTimer();
      wasLoggedIn.current = false;
    } else if (!userId && !token) {
      // No user and no token - ensure clean state
      stopTimer();
    }
  }, [userId, token]);
  
  // Setup tab visibility handling
  useEffect(() => {
    document.addEventListener('visibilitychange', handleTabVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleTabVisibilityChange);
    };
  }, []);
  
  // Listen for timer sync events from patient timer
  useEffect(() => {
    const handleTimerSync = (event: CustomEvent<{ source: string; isPaused: boolean }>) => {
      // Only sync if event came from patient timer
      if (event.detail.source === 'patient') {
        setGlobalPauseState(event.detail.isPaused);
      }
    };
    
    window.addEventListener('timerSync', handleTimerSync as EventListener);
    return () => {
      window.removeEventListener('timerSync', handleTimerSync as EventListener);
    };
  }, []);
  
  // Component cleanup on unmount - only for logout scenarios
  useEffect(() => {
    return () => {
      // Only clean up if user is not logged in (logout scenario)
      if (!userId && !token) {
        if (timerInterval) {
          clearInterval(timerInterval);
          timerInterval = null;
        }
        clearState();
      }
    };
  }, [userId, token]);
  
  // Ensure timer interval is running when component mounts or when timer should be running
  useEffect(() => {
    // Check every time the component renders if timer should be running but isn't
    if (globalState.isRunning && !timerInterval) {
      startTimer();
    }
  }); // No dependencies - check on every render
  
  // Return timer interface
  return {
    // State
    seconds: globalState.seconds,
    isRunning: globalState.isRunning,
    isPaused: globalState.isManuallyPaused || globalState.isTabInactive,
    isManuallyPaused: globalState.isManuallyPaused,
    isTabInactive: globalState.isTabInactive,
    
    // Formatted display
    formattedTime: formatTime(globalState.seconds),
    
    // Actions
    togglePause,
    
    // Visibility check
    isVisible: user ? hasTimelogAccess(user.type) : false,
  };
};

// Export global cleanup function for external use (e.g., AuthContext logout)
export const cleanupGlobalTimer = globalTimerCleanup;
